import {ChildPanelConfigComp} from '@wandb/weave/components/Panel2/ChildPanel';
import {
  usePanelInteractMode,
  useSelectedDocument,
} from '@wandb/weave/components/Panel2/PanelInteractContext';
import {PanelInteractDrawer} from '@wandb/weave/components/Sidebar/PanelInteractDrawer';
import {WeaveFeaturesContextProvider} from '@wandb/weave/context';
import {NotebookComputeGraphContextProvider} from '@wandb/weave/contextProviders';
import React, {memo, useMemo} from 'react';
import {Redirect} from 'react-router-dom';

import {ReportPageMetaTags} from '../../components/ReportPageMetaTags';
import {InstrumentedFloatingWaveLoader as Loader} from '../../components/utility/InstrumentedLoader';
import {GALLERY_POST_EDIT_PATH} from '../../routes/report/reportPaths';
import {useMemoizedSelector} from '../../state/hooks';
import * as ReportSelectors from '../../state/reports/selectors';
import {RunQueryContext} from '../../state/runs/context';
import {redirectToSignupThenBackHere} from '../../util/auth';
import {useInitializeRampFlagsStores} from '../../util/rampFeatureFlags/stores/useInitRampFlagsStore';
import * as Urls from '../../util/urls';
import {useWeaveFeatures} from '../../util/useWeaveFeatures';
import {NoAccessPage} from '../NoAccessPage';
import {ReportPageEditContainer} from './EditReportPage/ReportPageEditContainer';
import {useSetReportTitleAndDescription} from './hooks';
import {LoadedReportContextProvider} from './LoadedReportContext';
import NewUserFooter from './NewUserFooter';
import {ReportAdminBar} from './ReportAdminBar';
import ReportErrorBoundary from './ReportErrorBoundary';
import {useReportPageContext} from './ReportPageContext';
import {ReportMode} from './types';
import {ReportPageViewContainer} from './ViewReportPage/ReportPageViewContainer';

const ReportPageContainerComp = () => {
  const {
    isAdmin,
    entityName,
    projectName,
    reportMode,
    rootReportId,
    reportPageQuery,
    reportViewInfo,
    isWBFeaturedReport,
    viewer,
    reportParent,
    draftId,
    reportName,
    reportDrafts,
  } = useReportPageContext();
  const isInReadMode = reportMode === ReportMode.IS_VIEWING;
  const isInEditMode = reportMode === ReportMode.IS_EDITING;

  useSetReportTitleAndDescription(reportViewInfo.view);

  // Reports live outside the `MultiRunWorkspaceWithContext` tree, so they
  // need their own ramp-flag store init. `entityName` matches the value fed
  // into `RunQueryContext` below.
  useInitializeRampFlagsStores();

  const reportWidth = useMemoizedSelector(
    ReportSelectors.getReportWidth,
    reportViewInfo.reportViewRef
  );

  const weaveFeaturesForEditor = useWeaveFeatures({
    // TODO: Remove this flag once removed from Weave
    clientEvalInUseNodeValueEnabled: true,
    // TODO: Remove this flag once removed from Weave
    refinementInReactHooksDisabled: true,
    // TODO: Remove this flag once removed from Weave
    sidebarConfigStylingEnabled: true,
    // TODO: Remove this flag once removed from Weave
    errorBoundaryInPanelComp2Enabled: true,
    // TODO: Remove this flag once removed from Weave
    redesignedPlotConfigEnabled: true,
  });
  const weavePanelInteractMode = usePanelInteractMode();
  const isWeaveEditorActive = weavePanelInteractMode === 'config';
  const selectedWeavePanelProps = useSelectedDocument();

  const value = useMemo(
    () => ({
      entityName,
      projectName: projectName ?? '',
      report: {id: reportViewInfo.view?.id ?? rootReportId},
    }),
    [entityName, projectName, reportViewInfo.view?.id, rootReportId]
  );

  if (
    reportPageQuery.loading ||
    reportViewInfo.loading ||
    reportParent.loading ||
    reportDrafts.loading
  ) {
    return <Loader name="report-view-id-loader" size="huge" />;
  }

  if (
    reportViewInfo.noAccess &&
    isInEditMode &&
    reportParent.data?.viewParent != null
  ) {
    // if following new url structure, navigate to root report
    if (draftId != null) {
      return (
        <Redirect
          to={Urls.reportView({
            entityName,
            projectName: projectName ?? '',
            reportID: rootReportId,
            reportName: reportName,
          })}
        />
      );
    }
    // if following old url structure, navigate to fetched parent report
    // TODO: remove this when new url structure is fully launched
    return (
      <Redirect
        to={Urls.reportView({
          entityName,
          projectName: projectName ?? '',
          reportID: reportParent.data.viewParent.id,
          reportName: reportParent.data.viewParent.displayName ?? '',
        })}
      />
    );
  } else if (reportViewInfo.noAccess) {
    return <NoAccessPage />;
  }

  if (
    (!isWBFeaturedReport && // Check used to make sure published-work report paths will go through this path
      !GALLERY_POST_EDIT_PATH && // Check used to make sure users can edit the posts they want to submit
      projectName != null &&
      reportPageQuery.data == null) ||
    (isInEditMode && viewer == null) // Viewer has to be logged in to edit
  ) {
    // Get URL query params
    const urlParams = new URLSearchParams(window.location.search);
    // If `authRedirect` is set, redirect to the login page
    if (urlParams.get('authRedirect')) {
      // Redirect back to the current page after login (w/out query params)
      redirectToSignupThenBackHere(true);
    } else {
      return <NoAccessPage />;
    }
  }

  if (
    reportViewInfo == null ||
    reportViewInfo.reportViewRef == null ||
    reportViewInfo.view == null
  ) {
    // TODO - have a better error state.
    return (
      <div>
        Error loading report. Please try refreshing or contacting support.
      </div>
    );
  }

  // Do not try to render report if we don't have the relevant view ids available to use
  return (
    <LoadedReportContextProvider
      reportViewRef={reportViewInfo.reportViewRef}
      view={reportViewInfo.view}
      reportMode={reportMode}>
      {isInReadMode && <ReportPageMetaTags viewId={rootReportId} />}
      <ReportErrorBoundary editPage={isInEditMode}>
        <RunQueryContext.Provider value={value}>
          <div
            className={`report-page-container ${
              isInReadMode ? 'report-page-container--read-only' : ''
            }`}>
            <div
              className={`report-page page-tabs report-width-${reportWidth} ${
                isInReadMode ? 'read-only' : ''
              }`}>
              <div className="report-page-top">
                {isInReadMode && isAdmin && <ReportAdminBar />}
                {isInReadMode ? (
                  <ReportPageViewContainer />
                ) : (
                  <ReportPageEditContainer reportWidth={reportWidth} />
                )}
              </div>
              {isInReadMode && (
                <NewUserFooter whatFor="to create reports like this one." />
              )}
            </div>
            {selectedWeavePanelProps != null && (
              <PanelInteractDrawer active={isWeaveEditorActive} sticky>
                <NotebookComputeGraphContextProvider>
                  <WeaveFeaturesContextProvider
                    features={weaveFeaturesForEditor}>
                    <ChildPanelConfigComp {...selectedWeavePanelProps} />
                  </WeaveFeaturesContextProvider>
                </NotebookComputeGraphContextProvider>
              </PanelInteractDrawer>
            )}
          </div>
        </RunQueryContext.Provider>
      </ReportErrorBoundary>
    </LoadedReportContextProvider>
  );
};

export const ReportPageContainer = memo(ReportPageContainerComp);
